import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'

import { Input } from '@/components/ui/Input'
import { useDebounce } from '@/hooks/useDebounce'
import { useNameSuggestions } from '@/hooks/useJudgments'
import { cn } from '@/lib/cn'

export interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

/**
 * Keyword box with typeahead over names already in the data set.
 *
 * The text is committed on a debounce so typing does not fire a request per
 * keystroke, and Enter commits immediately.
 *
 * The combobox wiring is complete rather than nominal: the listbox has an id
 * the input points at, each option has an id, and the highlighted option is
 * named by `aria-activedescendant`. Without those three a screen reader is
 * told a combobox exists and then never hears a suggestion.
 */
export function SearchBar({
  value,
  onChange,
  placeholder = 'Search a name (SMITH, JOHN) or a judgment or docket number',
}: SearchBarProps) {
  const [draft, setDraft] = useState(value)
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const debounced = useDebounce(draft, 350)
  const { data: suggestions = [] } = useNameSuggestions(open ? debounced : '')

  // Keep the box in step when the URL changes underneath (back button, reset).
  useEffect(() => {
    setDraft(value)
  }, [value])

  // Commit the debounced text; skip when it already matches the committed value.
  useEffect(() => {
    if (debounced !== value) onChange(debounced)
    // `onChange` is stable via useSearchState's useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  const commit = (next: string) => {
    setDraft(next)
    onChange(next)
    setOpen(false)
    setHighlighted(-1)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit(highlighted >= 0 ? suggestions[highlighted] : draft)
      return
    }
    if (event.key === 'Escape') {
      setOpen(false)
      setHighlighted(-1)
      return
    }
    if (suggestions.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((current) => (current + 1) % suggestions.length)
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((current) => (current <= 0 ? suggestions.length - 1 : current - 1))
    }
  }

  const showSuggestions = open && suggestions.length > 0

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <Input
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value)
          setOpen(true)
          setHighlighted(-1)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        leading={<SearchIcon />}
        className="h-11 pl-9.5 text-base"
        role="combobox"
        aria-expanded={showSuggestions}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Search judgments"
        aria-activedescendant={
          highlighted >= 0 ? `${listId}-option-${highlighted}` : undefined
        }
        trailing={
          draft ? (
            <button
              type="button"
              onClick={() => commit('')}
              aria-label="Clear search"
              className="rounded-full p-1 text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          ) : undefined
        }
      />

      {showSuggestions ? (
        <ul
          role="listbox"
          id={listId}
          aria-label="Name suggestions"
          className="animate-rise scrollbar-slim absolute top-full right-0 left-0 z-(--z-dropdown) mt-1.5 max-h-64 overflow-y-auto rounded-xl border border-line bg-surface-raised py-1 shadow-lg"
        >
          {suggestions.map((name, index) => (
            <li key={name}>
              <button
                type="button"
                role="option"
                id={`${listId}-option-${index}`}
                aria-selected={index === highlighted}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => commit(name)}
                className={cn(
                  'block w-full px-3 py-2 text-left text-base transition-colors',
                  index === highlighted
                    ? 'bg-accent-soft text-ink'
                    : 'text-ink-secondary hover:bg-surface-hover',
                )}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
